<template>
  <div v-if="visible" class="announcement-overlay" @click.self="handleClose">
    <div class="announcement-dialog">
      <div class="announcement-header">
        <div class="announcement-title">
          <span class="title-icon">{{ announcement.type === 2 ? '🎉' : '📢' }}</span>
          <span class="title-text">{{ announcement.title }}</span>
        </div>
        <button class="close-btn" @click="handleClose">×</button>
      </div>
      
      <div class="announcement-content">
        <div class="content-text" v-html="formatContent(announcement.content)"></div>
        <div v-if="announcement.publishTime || announcement.publish_time" class="publish-time">
          发布时间：{{ formatTime(announcement.publishTime || announcement.publish_time) }}
        </div>
      </div>
      
      <div class="announcement-footer">
        <label class="checkbox-label">
          <input 
            type="checkbox" 
            v-model="dontShowToday"
            @change="handleCheckboxChange"
          />
          <span>今日不再显示</span>
        </label>
        <button class="confirm-btn" @click="handleClose">我知道了</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  announcement: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:visible', 'close'])

const dontShowToday = ref(false)

// 格式化内容（支持换行）
function formatContent(content) {
  if (!content) return ''
  // 转义HTML，然后替换换行为<br>
  return content
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>')
}

// 格式化时间
function formatTime(timeStr) {
  if (!timeStr) return ''
  const date = new Date(timeStr)
  return date.toLocaleString('zh-CN')
}

// 处理复选框变化
function handleCheckboxChange() {
  if (dontShowToday.value && props.announcement.id) {
    // 保存到localStorage，格式：announcement_hide_日期_公告ID
    const today = new Date().toISOString().split('T')[0] // YYYY-MM-DD
    const key = `announcement_hide_${today}_${props.announcement.id}`
    localStorage.setItem(key, 'true')
  }
}

// 关闭弹窗
function handleClose() {
  emit('update:visible', false)
  emit('close')
}

// 检查今日是否已隐藏
function checkIfHiddenToday() {
  if (!props.announcement.id) return false
  const today = new Date().toISOString().split('T')[0]
  const key = `announcement_hide_${today}_${props.announcement.id}`
  return localStorage.getItem(key) === 'true'
}

// 监听visible变化
watch(() => props.visible, (newVal) => {
  if (newVal) {
    // 弹窗显示时，检查是否已隐藏
    dontShowToday.value = checkIfHiddenToday()
  }
})
</script>

<style scoped>
.announcement-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.3s;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.announcement-dialog {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s;
  overflow: hidden;
}

@keyframes slideUp {
  from {
    transform: translateY(50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.announcement-header {
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.1);
}

.announcement-title {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.title-icon {
  font-size: 24px;
}

.title-text {
  color: white;
  font-size: 18px;
  font-weight: bold;
  flex: 1;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 28px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  line-height: 1;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

.announcement-content {
  padding: 20px;
  flex: 1;
  overflow-y: auto;
  color: white;
}

.content-text {
  font-size: 15px;
  line-height: 1.8;
  margin-bottom: 15px;
  word-wrap: break-word;
  white-space: pre-wrap;
}

.publish-time {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 10px;
}

.announcement-footer {
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.1);
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
  font-size: 14px;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.confirm-btn {
  background: white;
  color: #667eea;
  border: none;
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.confirm-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(255, 255, 255, 0.3);
}

.confirm-btn:active {
  transform: scale(0.95);
}
</style>

